import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Plus,
  CheckCircle,
  XCircle,
  Calendar,
  Upload,
  Edit,
  Trash2,
  X,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Spinner } from "../components/ui/shadcn-io/spinner";
import { HolidayCalendar } from "../components/holidayCalendar";

interface Holiday {
  _id: string;
  date: string;
  name: string;
  year?: number;
  state?: string;
  isActive?: boolean;
}

export default function AdminHolidays() {
  const location = useLocation();
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [allYearHolidays, setAllYearHolidays] = useState<Holiday[]>([]);
  const [showInactive, setShowInactive] = useState(false);

  // Dialog states
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState<Holiday | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    date: "",
    name: "",
    state: "",
    isActive: true,
  });

  const [bulkData, setBulkData] = useState({
    year: new Date().getFullYear(),
    holidays: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      window.history.replaceState(null, "");

      setTimeout(() => {
        setSuccessMessage("");
      }, 5000);
    }
  }, [location.state]);

  useEffect(() => {
    fetchHolidays();
  }, [selectedYear, currentDate]);

  const fetchHolidays = async () => {
    try {
      setIsLoading(true);
      setError("");
      const token = localStorage.getItem("authToken");
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1; // getMonth() returns 0-11, API expects 1-12
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/holidays?year=${year}&month=${month}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setHolidays(
        Array.isArray(response.data)
          ? response.data
          : response.data?.holidays || []
      );
    } catch (err: any) {
      console.error("Error fetching holidays:", err);
      setError(
        err.response?.data?.message || "Failed to fetch public holidays"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllYearHolidays = async (year: number) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/holidays?year=${year}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setAllYearHolidays(
        Array.isArray(response.data)
          ? response.data
          : response.data?.holidays || []
      );
    } catch (err: any) {
      console.error("Error fetching all year holidays:", err);
    }
  };

  useEffect(() => {
    fetchAllYearHolidays(selectedYear);
  }, [selectedYear]);

  const formatDateToYYYYMMDD = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleDateClick = (date: Date, holiday?: Holiday) => {
    if (holiday) {
      // Edit existing holiday
      setSelectedHoliday(holiday);
      setFormData({
        date: new Date(holiday.date).toISOString().split("T")[0],
        name: holiday.name,
        state: holiday.state || "",
        isActive: holiday.isActive !== false,
      });
      setIsEditDialogOpen(true);
    } else {
      // Add new holiday - use local date formatting to avoid timezone issues
      setSelectedDate(date);
      setFormData({
        date: formatDateToYYYYMMDD(date),
        name: "",
        state: "",
        isActive: true,
      });
      setIsAddDialogOpen(true);
    }
  };

  const handleAddHoliday = async () => {
    if (!formData.date || !formData.name) {
      setError("Date and name are required");
      return;
    }

    try {
      setIsSubmitting(true);
      setError("");
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}api/holidays`,
        {
          date: formData.date,
          name: formData.name,
          year: new Date(formData.date).getFullYear(),
          state: formData.state || undefined,
          isActive: formData.isActive,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Backend may reactivate an existing inactive holiday
      const message = response.data?.message || "Holiday added successfully!";
      setSuccessMessage(message);
      setIsAddDialogOpen(false);
      resetForm();
      fetchHolidays();
      fetchAllYearHolidays(selectedYear);
    } catch (err: any) {
      console.error("Error adding holiday:", err);
      setError(err.response?.data?.message || "Failed to add holiday");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBulkAdd = async () => {
    try {
      setIsSubmitting(true);
      setError("");
      const token = localStorage.getItem("authToken");

      // Parse the holidays text
      const lines = bulkData.holidays.split("\n").filter((line) => line.trim());
      const parsedHolidays = lines.map((line) => {
        const parts = line.split(",").map((p) => p.trim());
        const holiday: any = {};
        if (parts[0]) holiday.date = parts[0];
        if (parts[1]) holiday.name = parts[1];
        if (parts[2]) holiday.state = parts[2];
        return holiday;
      });

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}api/holidays/bulk`,
        {
          year: bulkData.year,
          holidays: parsedHolidays,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSuccessMessage("Holidays added successfully!");
      setIsBulkDialogOpen(false);
      setBulkData({ year: new Date().getFullYear(), holidays: "" });
      fetchHolidays();
    } catch (err: any) {
      console.error("Error bulk adding holidays:", err);
      setError(err.response?.data?.message || "Failed to add holidays");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditHoliday = async () => {
    if (!selectedHoliday || !formData.date || !formData.name) {
      setError("Date and name are required");
      return;
    }

    try {
      setIsSubmitting(true);
      setError("");
      const token = localStorage.getItem("authToken");
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}api/holidays/${selectedHoliday._id}`,
        {
          date: formData.date,
          name: formData.name,
          state: formData.state || undefined,
          isActive: formData.isActive,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSuccessMessage("Holiday updated successfully!");
      setIsEditDialogOpen(false);
      setSelectedHoliday(null);
      resetForm();
      fetchHolidays();
      fetchAllYearHolidays(selectedYear);
    } catch (err: any) {
      console.error("Error updating holiday:", err);
      setError(err.response?.data?.message || "Failed to update holiday");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteHoliday = async () => {
    if (!selectedHoliday) return;

    try {
      setIsSubmitting(true);
      setError("");
      const token = localStorage.getItem("authToken");
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}api/holidays/${selectedHoliday._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSuccessMessage("Holiday deleted successfully!");
      setIsDeleteDialogOpen(false);
      setSelectedHoliday(null);
      fetchHolidays();
    } catch (err: any) {
      console.error("Error deleting holiday:", err);
      setError(err.response?.data?.message || "Failed to delete holiday");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      date: "",
      name: "",
      state: "",
      isActive: true,
    });
    setSelectedDate(null);
  };

  const openDeleteDialog = (holiday: Holiday) => {
    setSelectedHoliday(holiday);
    setIsDeleteDialogOpen(true);
  };

  const activeHolidays = holidays.filter((h) => h.isActive !== false);
  const inactiveHolidays = holidays.filter((h) => h.isActive === false);
  // Include all holidays (active and inactive) in calendar so inactive ones can be clicked and edited
  const allHolidaysForCalendar = [...activeHolidays, ...inactiveHolidays];
  const allYearActiveHolidays = allYearHolidays.filter(
    (h) => h.isActive !== false
  );
  const allYearInactiveHolidays = allYearHolidays.filter(
    (h) => h.isActive === false
  );
  const displayedHolidays = showInactive
    ? allYearInactiveHolidays
    : allYearActiveHolidays;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-2">
          <Spinner variant="ellipsis" size={40} />
          <p className="text-sm text-muted-foreground">Loading holidays...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">Public Holidays</h1>
          <p className="text-muted-foreground mt-1">
            Manage public holidays calendar
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setIsBulkDialogOpen(true)}>
            <Upload className="mr-2 h-4 w-4" />
            Bulk Add
          </Button>
          <Button onClick={() => setIsAddDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Holiday
          </Button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-green-800 font-medium">{successMessage}</p>
          </div>
          <button
            onClick={() => setSuccessMessage("")}
            className="text-green-600 hover:text-green-800"
          >
            ×
          </button>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-800 font-medium">{error}</p>
          </div>
          <button
            onClick={() => setError("")}
            className="text-red-600 hover:text-red-800"
          >
            ×
          </button>
        </div>
      )}

      {/* Year Selector */}
      <div className="mb-6">
        <Select
          value={selectedYear.toString()}
          onValueChange={(value) => {
            const newYear = parseInt(value);
            setSelectedYear(newYear);
            // Navigate to January (month 0) of the selected year
            setCurrentDate(new Date(newYear, 0, 1));
            fetchAllYearHolidays(newYear);
          }}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Select year" />
          </SelectTrigger>
          <SelectContent>
            {Array.from({ length: 5 }, (_, i) => {
              const year = new Date().getFullYear() + i;
              return (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      {/* Calendar View */}
      <Card>
        <CardHeader>
          <CardTitle>Holiday Calendar</CardTitle>
        </CardHeader>
        <CardContent>
          <HolidayCalendar
            holidays={allHolidaysForCalendar}
            currentDate={currentDate}
            onDateChange={setCurrentDate}
            onDateClick={handleDateClick}
          />
        </CardContent>
      </Card>

      {/* Add Holiday Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Public Holiday</DialogTitle>
            <DialogDescription>
              Add a new public holiday to the calendar
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-3">
              <Label htmlFor="date">
                Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) =>
                  setFormData({ ...formData, date: e.target.value })
                }
                required
              />
            </div>
            <div className="grid gap-3">
              <Label htmlFor="name">
                Holiday Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="e.g., New Year's Day"
                required
              />
            </div>
            <div className="grid gap-3">
              <Label htmlFor="state">State (Optional)</Label>
              <Input
                id="state"
                value={formData.state}
                onChange={(e) =>
                  setFormData({ ...formData, state: e.target.value })
                }
                placeholder="e.g., Maharashtra"
              />
              <p className="text-xs text-muted-foreground">
                Leave empty for national holidays
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsAddDialogOpen(false);
                resetForm();
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button onClick={handleAddHoliday} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner variant="ellipsis" size={16} className="mr-2" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Holiday
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Add Dialog */}
      <Dialog open={isBulkDialogOpen} onOpenChange={setIsBulkDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bulk Add Holidays</DialogTitle>
            <DialogDescription>
              Add multiple holidays at once. Format: date,name,state (one per
              line)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-3">
              <Label htmlFor="bulkYear">Year</Label>
              <Input
                id="bulkYear"
                type="number"
                value={bulkData.year}
                onChange={(e) =>
                  setBulkData({
                    ...bulkData,
                    year: parseInt(e.target.value),
                  })
                }
              />
            </div>
            <div className="grid gap-3">
              <Label htmlFor="bulkHolidays">
                Holidays <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="bulkHolidays"
                value={bulkData.holidays}
                onChange={(e) =>
                  setBulkData({ ...bulkData, holidays: e.target.value })
                }
                placeholder="2026-01-01,New Year's Day&#10;2026-01-26,Republic Day&#10;2026-05-01,Maharashtra Day,Maharashtra"
                rows={10}
                required
              />
              <p className="text-xs text-muted-foreground">
                Format: YYYY-MM-DD,Holiday Name,State (optional). One holiday
                per line.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsBulkDialogOpen(false);
                setBulkData({ year: new Date().getFullYear(), holidays: "" });
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button onClick={handleBulkAdd} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner variant="ellipsis" size={16} className="mr-2" />
                  Adding...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Bulk Add
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Holiday Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Public Holiday</DialogTitle>
            <DialogDescription>Update holiday information</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-3">
              <Label htmlFor="editDate">
                Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="editDate"
                type="date"
                value={formData.date}
                onChange={(e) =>
                  setFormData({ ...formData, date: e.target.value })
                }
                required
              />
            </div>
            <div className="grid gap-3">
              <Label htmlFor="editName">
                Holiday Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="editName"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
              />
            </div>
            <div className="grid gap-3">
              <Label htmlFor="editState">State (Optional)</Label>
              <Input
                id="editState"
                value={formData.state}
                onChange={(e) =>
                  setFormData({ ...formData, state: e.target.value })
                }
                placeholder="e.g., Maharashtra"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) =>
                  setFormData({ ...formData, isActive: e.target.checked })
                }
                className="rounded"
              />
              <Label htmlFor="isActive">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsEditDialogOpen(false);
                setSelectedHoliday(null);
                resetForm();
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button onClick={handleEditHoliday} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner variant="ellipsis" size={16} className="mr-2" />
                  Updating...
                </>
              ) : (
                <>
                  <Edit className="mr-2 h-4 w-4" />
                  Update
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Holiday</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this holiday? This action cannot
              be undone.
            </DialogDescription>
          </DialogHeader>
          {selectedHoliday && (
            <div className="p-3 bg-muted rounded-md space-y-2">
              <p>
                <span className="font-medium">Holiday:</span>{" "}
                {selectedHoliday.name}
              </p>
              <p>
                <span className="font-medium">Date:</span>{" "}
                {new Date(selectedHoliday.date).toLocaleDateString()}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setSelectedHoliday(null);
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteHoliday}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Spinner variant="ellipsis" size={16} className="mr-2" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
